/**
 * Kontrast rechnen — und eine für hell gewählte Farbe für dunkel umrechnen.
 *
 * **Warum das eine eigene Stelle ist.** Die Farben dieser Anwendung sind
 * einstellbar: Sprechblasen je Reise und je Konto (#62), die Zeilen der
 * Übersicht (#54), der Grund des Chats. Eingestellt werden sie im **hellen**
 * Erscheinungsbild, und damit steht bei jeder einzelnen dieselbe Frage: was
 * ist darauf lesbar, und was wird daraus, wenn das Gerät abends auf dunkel
 * schaltet? Zwei Antworten an zwei Stellen liefen auseinander — deshalb hier.
 *
 * **Gerechnet wird nach WCAG**, also über die relative Leuchtdichte mit ihrer
 * Gammakorrektur. Vorher stand in `blasenfarben.ts` eine BT.601-Helligkeit
 * gegen einen festen Schwellenwert von 150. Das sieht aus wie eine
 * Kontrastrechnung und ist keine: gemessen an einem Bildschirmfoto lag die
 * eingestellte Blase bei `#52b5c2` — BT.601 sagt 137, also knapp unter dem
 * Schwellenwert, also weiße Schrift. Weiß darauf sind **2,40:1**, Schwarz
 * wären **8,00:1** gewesen. Der Schwellenwert kannte den Unterschied nicht.
 */

/** Eine Farbe als drei Werte von 0 bis 255 — oder `null`, wenn sie keine ist. */
function zerlege(hex: string): [number, number, number] | null {
  const roh = hex.trim().replace('#', '')
  const voll = roh.length === 3 ? roh.split('').map((z) => z + z).join('') : roh
  if (!/^[0-9a-fA-F]{6}$/.test(voll)) return null
  const zahl = Number.parseInt(voll, 16)
  return [(zahl >> 16) & 255, (zahl >> 8) & 255, zahl & 255]
}

function alsHex(r: number, g: number, b: number): string {
  const zwei = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0')
  return `#${zwei(r)}${zwei(g)}${zwei(b)}`
}

/**
 * Die relative Leuchtdichte nach WCAG 2.
 *
 * Die Gammakorrektur ist der Unterschied zur naiven Rechnung: ein Kanal wird
 * erst in lineares Licht zurückgerechnet und dann gewichtet. Ohne sie kommen
 * gerade in der Mitte des Bereichs die falschen Urteile heraus, und dort
 * liegen die Farben, die jemand tatsächlich wählt.
 */
export function leuchtdichte(hex: string): number {
  const teile = zerlege(hex)
  if (teile === null) return 0
  const [r, g, b] = teile.map((v) => {
    const s = v / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Das Kontrastverhältnis zweier Farben: 1 heißt gleich, 21 ist Schwarz zu Weiß. */
export function kontrast(a: string, b: string): number {
  const x = leuchtdichte(a)
  const y = leuchtdichte(b)
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05)
}

/**
 * Die Grenze, unter der Weiß auf einer Farbe nicht mehr genügt.
 *
 * **3 und nicht 4,5**, und das ist eine Abwägung mit einem Namen: Apple selbst
 * setzt Weiß auf sein Systemblau `#007aff`, und das sind 4,02:1 — unter der
 * WCAG-Grenze für Fließtext. Mit 4,5 bekäme die **Vorgabe** dieser Anwendung
 * schwarze Schrift auf blauer Blase, und das sähe nach einem Fehler aus,
 * obwohl es die Regel wäre. 3 lässt die Vorgabe, wie sie ist, und greift dort,
 * wo es wirklich weh tut.
 */
export const WEISS_GRENZE = 3

/**
 * Schwarz oder Weiß — was auf dieser Farbe lesbar ist.
 *
 * **Weiß hat den Vortritt und muss ihn sich verdienen.** Eine farbige Fläche
 * mit weißer Schrift ist die Erwartung — so sieht jeder Knopf und jede
 * gefüllte Sprechblase aus. Erst wenn Weiß unter `WEISS_GRENZE` fällt, wird
 * Schwarz genommen; die reine „nimm den höheren Wert"-Regel hätte auch dem
 * Systemblau schwarze Schrift gegeben (5,23 gegen 4,02) und damit die
 * Erwartung gegen zwei Zehntel eingetauscht.
 */
export function schriftAuf(hex: string): string {
  if (zerlege(hex) === null) return '#000000'
  return kontrast(hex, '#ffffff') >= WEISS_GRENZE ? '#ffffff' : '#000000'
}

/**
 * Dieselbe Schriftfarbe, nur zurückgenommen — für Uhrzeit, Haken, Datum.
 *
 * **Kein zweites Helligkeitsurteil.** Was `schriftAuf()` sagt, gilt auch hier,
 * nur durchsichtig; zwei Schwellenwerte nebeneinander liefen irgendwann
 * auseinander.
 *
 * **Die zwei Werte sind verschieden, und das ist kein Versehen:** Weiß auf
 * dunklem Grund braucht mehr Deckung als Schwarz auf hellem, sonst verschwindet
 * es. Dieselbe Beobachtung steckt in den zwei Fassungen von `--text-muted`.
 */
export function gedaempftAuf(hex: string): string {
  return schriftAuf(hex) === '#ffffff'
    ? 'rgba(255, 255, 255, 0.72)'
    : 'rgba(17, 27, 33, 0.55)'
}

// --- Von hell nach dunkel --------------------------------------------------

function nachHsl(hex: string): { h: number; s: number; l: number } | null {
  const teile = zerlege(hex)
  if (teile === null) return null
  const [r, g, b] = teile.map((v) => v / 255) as [number, number, number]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return { h, s, l }
}

function ausHsl(h: number, s: number, l: number): string {
  if (s === 0) return alsHex(l * 255, l * 255, l * 255)
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s
  const p = 2 * l - q
  const kanal = (t: number) => {
    let x = t
    if (x < 0) x += 1
    if (x > 1) x -= 1
    if (x < 1 / 6) return p + (q - p) * 6 * x
    if (x < 1 / 2) return q
    if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6
    return p
  }
  return alsHex(kanal(h + 1 / 3) * 255, kanal(h) * 255, kanal(h - 1 / 3) * 255)
}

/**
 * Dieselbe Farbe, für den dunklen Modus — **der Ton bleibt, die Helligkeit
 * wird gedreht** (#73).
 *
 * **Aus dem Betrieb, mit drei Bildschirmfotos:** eines hell, eines dunkel,
 * eines dunkel mit der Browser-Erweiterung *Noir*. In der dunklen Fassung ohne
 * Noir standen die Zeilen der Übersicht mit dem eingestellten `#d7e3fe` da und
 * trugen weiße Schrift — gemessen **1,29:1**. Noir macht daraus `#1e2426` mit
 * heller Schrift, **12,84:1**. Genau das wird hier nachgebaut.
 *
 * Drei Schritte, und jeder hat einen Grund:
 *
 *  - **Der Farbton bleibt unangetastet.** Wer sich Blau eingestellt hat, soll
 *    abends Blau sehen und nicht Grau. Das ist der Unterschied zwischen
 *    „umgerechnet" und „weggenommen".
 *  - **Die Sättigung wird gedeckelt.** Dieselbe Sättigung wirkt auf dunklem
 *    Grund viel lauter als auf hellem; ungedeckelt wird aus einem zarten
 *    Hellblau ein leuchtendes Marineblau. Noir geht hier noch weiter herunter
 *    und nimmt der Farbe fast alles — das ist für eine Erweiterung richtig,
 *    die jede fremde Seite verträglich machen muss, und für eine eigene
 *    Anwendung zu viel.
 *  - **Die Helligkeit landet in einem schmalen dunklen Band.** Nicht `1 - l`:
 *    aus Weiß würde sonst Schwarz, und eine Zeile wäre von der Seite darunter
 *    nicht mehr zu unterscheiden. Das Band beginnt über der Seitenfarbe, damit
 *    eine Zeile eine Zeile bleibt.
 *
 * Eine ungültige Angabe kommt unverändert zurück — hier wird nichts geraten.
 */
export const DUNKEL_BAND = { unten: 0.11, spanne: 0.16, sattheit: 0.4 }

export function fuerDunkel(hex: string): string {
  const hsl = nachHsl(hex)
  if (hsl === null) return hex
  const s = Math.min(hsl.s, DUNKEL_BAND.sattheit)
  const l = DUNKEL_BAND.unten + (1 - hsl.l) * DUNKEL_BAND.spanne
  return ausHsl(hsl.h, s, l)
}
